import React, {useEffect, useState} from 'react';
import Loading from "../../../../../ui/Loading";
import {useToast} from "../../../../../../utils/contexts/toast-context";
import {useTranslation} from "react-i18next";
import {Box, Popover, TextField} from "@mui/material";
import MRTMuiButton from "../../../../../ui/mui/mui_react_table/MRTMuiButton";
import {Add, Edit} from "@mui/icons-material";
import MuiAutocomplete from "../../../../../ui/mui/mui-input/MuiAutoComplete";
import {useTheme} from "@mui/material/styles";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
    IMyCompanyBank
} from "../../../../../../utils/interfaces/my_company/my_company_registry/my_company_bank/IMyCompanyBank";
import useApiGetMyCompanyBanks from "./api-hooks/useApiGetMyCompanyBanks";
import useApiGetMyCompanyBank from "./api-hooks/useApiGetMyCompanyBank";
import MyCompanyBankForm from "./MyCompanyBankForm";
import MyCompanyBankTable from "./MyCompanyBankTable";
import MuiModal from "../../../../../ui/mui/mui_modal/MuiModal";


interface Props {
    obj: IMyCompanyBank | undefined,
    onChange: (obj: IMyCompanyBank | undefined) => void,
    className: string,
    label?: string,
    required?: boolean
}

const MyCompanyBankSelect = ({
                                 obj: initialObj,
                                 label,
                                 onChange,
                                 required,
                                 ...props
                             }: Props) => {

    const {displayError} = useToast()
    const theme = useTheme();
    const {t} = useTranslation()

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)
    const [isListTableOpen, setIsListTableOpen] = useState<boolean>(false)


    const [objs, setObjs] = useState<IMyCompanyBank[]>([]);
    const [obj, setObj] = useState<IMyCompanyBank | undefined>(initialObj)


    useEffect(() => {
        setObj(undefined);
        if (initialObj) {
            setObj(initialObj)
        }
    }, [initialObj]);


    const {
        data,
        isLoading,
    } = useApiGetMyCompanyBanks();


    useEffect(() => {
        if (data) {
            setObjs(data)
        }
    }, [data]);


    const {
        data: detailedObj,
    } = useApiGetMyCompanyBank(obj?.id);


    useEffect(() => {
        if (detailedObj) {
            setObj(detailedObj);
        }
    }, [detailedObj]);

    const handleOnChange = (event: React.SyntheticEvent, newValue: IMyCompanyBank | null) => {
        if (newValue) {
            onChange(newValue);
        } else {
            onChange(undefined);
        }
    };

    useEffect(() => {
        onChange(obj);
    }, [obj]);


    return (
        <>
            {isLoading ? (
                <Loading/>
            ) : (
                <>
                    <Box sx={{alignItems: 'center', width: '100%'}}>
                        <MuiAutocomplete
                            id="bank-select"
                            options={objs}
                            getOptionLabel={(option) => option.bank.name + ' - ' + option.iban}
                            value={detailedObj ? detailedObj : null}
                            onChange={handleOnChange}
                            isOptionEqualToValue={(option, value) => option.id === value?.id && option.iban === value?.iban}
                            renderInput={(params) => (
                                <TextField
                                    sx={{textTransform: 'capitalize'}}
                                    {...params}
                                    label={label ? t(label) : '' || t('bank')}
                                    required={required}
                                />
                            )}
                            borderColor={theme.palette.primary.light}
                            onFocusBorderColor={theme.palette.primary.dark}
                            minWidth={300}
                        >
                            <MRTMuiButton
                                icon={<FormatListBulletedIcon sx={{color: theme.palette.primary.main}}/>}
                                onClick={() => setIsListTableOpen(true)}
                                small={true}
                                label={'show_list'}
                            />
                            <MRTMuiButton
                                icon={obj ? <Edit sx={{color: theme.palette.primary.main}}/> :
                                    <Add sx={{color: theme.palette.primary.main}}/>}
                                onClick={() => setIsEditFormOpen(true)}
                                small={true}
                                label={obj ? 'edit_bank' : 'add_bank'}
                            />
                        </MuiAutocomplete>
                    </Box>

                    <Popover
                        id={'edit_popover'}
                        open={isEditFormOpen}
                        anchorEl={anchorEl}
                        onClose={() => setIsEditFormOpen(false)}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'center',
                        }}
                    >
                        <MyCompanyBankForm objToEdit={obj ? obj : null}
                                           onSelect={onChange}
                                           onClose={() => setIsEditFormOpen(false)}
                        />
                    </Popover>

                    <MuiModal top="50%" left="50%" width={'120vh'} open={isListTableOpen}
                              onClose={() => setIsListTableOpen(false)}
                              disableBackdropClick={true}
                    >
                        <MyCompanyBankTable
                            alreadySelectedObj={obj}
                            onSelect={onChange}
                            onClose={() => setIsListTableOpen(false)}
                        />
                    </MuiModal>


                </>
            )}
        </>
    );
}

export default MyCompanyBankSelect;